import { forwardRef, useState } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';
import styles from './Avatar.module.scss';

// ============================================
// TYPES
// ============================================

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type AvatarColor = 'green' | 'red' | 'purple' | 'yellow' | 'blue' | 'grey';

export interface AvatarProps extends HTMLAttributes<HTMLSpanElement> {
  /** Image source URL */
  src?: string;
  /** Alt text for the image */
  alt?: string;
  /** Display name — used for initials fallback and aria-label */
  name?: string;
  /** Avatar size */
  size?: AvatarSize;
  /** Background color for initials (auto-assigned from name if omitted) */
  color?: AvatarColor;
  /** Custom initials override (otherwise derived from name) */
  initials?: string;
  /** Primary text displayed beside the avatar */
  label?: string;
  /** Secondary text displayed below the label */
  subtitle?: string;
}

// ============================================
// HELPERS
// ============================================

const COLORS: AvatarColor[] = ['green', 'red', 'purple', 'yellow', 'blue', 'grey'];

/** Derive two-letter initials from a full name */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '';
  if (parts.length === 1) return (parts[0]?.[0] ?? '').toUpperCase();
  return `${parts[0]?.[0] ?? ''}${parts[parts.length - 1]?.[0] ?? ''}`.toUpperCase();
}

/** Deterministic color from a name string */
function getColorFromName(name: string): AvatarColor {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % COLORS.length;
  return COLORS[index]!;
}

// ============================================
// AVATAR COMPONENT
// ============================================

const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(
  (
    {
      src,
      alt,
      name = '',
      size = 'md',
      color,
      initials,
      label,
      subtitle,
      className,
      ...rest
    },
    ref,
  ) => {
    const [imgError, setImgError] = useState(false);
    const showImage = src && !imgError;
    const resolvedColor = color ?? getColorFromName(name);
    const resolvedInitials = initials ?? getInitials(name);
    const isLarge = size === 'lg' || size === 'xl';

    const avatarElement = (
      <span
        ref={!label ? ref : undefined}
        className={[
          styles.avatar,
          styles[size],
          !showImage ? styles[`color-${resolvedColor}${isLarge ? '-strong' : ''}`] : undefined,
          !label ? className : undefined,
        ]
          .filter(Boolean)
          .join(' ')}
        role="img"
        aria-label={(alt ?? name) || undefined}
        {...(!label ? rest : {})}
      >
        {showImage ? (
          <img
            className={styles.image}
            src={src}
            alt={alt ?? name}
            onError={() => setImgError(true)}
          />
        ) : (
          <span className={styles.initials}>{resolvedInitials}</span>
        )}
      </span>
    );

    // Standalone avatar — no text
    if (!label) return avatarElement;

    // Avatar with text
    return (
      <span
        ref={ref}
        className={[styles.wrapper, styles[`wrapper-${size}`], className]
          .filter(Boolean)
          .join(' ')}
        {...rest}
      >
        {avatarElement}
        <span className={styles.textBlock}>
          <span className={styles.name}>{label}</span>
          {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
        </span>
      </span>
    );
  },
);

Avatar.displayName = 'Avatar';

export default Avatar;

// ============================================
// AVATAR GROUP
// ============================================

export type OverflowStyle = 'default' | 'brand';

export interface AvatarGroupProps extends HTMLAttributes<HTMLDivElement> {
  /** Avatar size for all items */
  size?: AvatarSize;
  /** Maximum number of avatars to show before overflow */
  max?: number;
  /** List of avatar data */
  avatars: Array<{
    src?: string;
    name: string;
    color?: AvatarColor;
  }>;
  /** Overflow counter style */
  overflowStyle?: OverflowStyle;
  /** Render function for custom overflow content */
  renderOverflow?: (count: number) => ReactNode;
}

export const AvatarGroup = forwardRef<HTMLDivElement, AvatarGroupProps>(
  (
    {
      size = 'sm',
      max = 4,
      avatars,
      overflowStyle = 'default',
      renderOverflow,
      className,
      ...rest
    },
    ref,
  ) => {
    const visible = avatars.slice(0, max);
    const overflowCount = avatars.length - max;

    const itemClass = [
      styles.groupItem,
      size === 'md' ? styles['groupItem-md'] : undefined,
      size === 'lg' ? styles['groupItem-lg'] : undefined,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div
        ref={ref}
        className={[styles.group, className].filter(Boolean).join(' ')}
        role="group"
        aria-label={`${avatars.length} people`}
        {...rest}
      >
        {visible.map((avatar, i) => (
          <span key={i} className={itemClass}>
            <Avatar
              src={avatar.src}
              name={avatar.name}
              color={avatar.color}
              size={size}
            />
          </span>
        ))}

        {overflowCount > 0 &&
          (renderOverflow ? (
            renderOverflow(overflowCount)
          ) : (
            <span
              className={[
                styles.overflow,
                styles[`overflow-${size}`],
                styles[`overflow-${overflowStyle}`],
              ].join(' ')}
              aria-label={`${overflowCount} more`}
            >
              +{overflowCount}
            </span>
          ))}
      </div>
    );
  },
);

AvatarGroup.displayName = 'AvatarGroup';
